import { Controller } from "@hotwired/stimulus"
import Tagify from "@yaireo/tagify"

// Connects to data-controller="tagify"
export default class extends Controller {
  static targets = ["input"]
  static values = {
    whitelist: Array,
    placeholder: String,
    maxTags: Number,
    dropdown: Object,
    enforceWhitelist: Boolean,
    duplicates: Boolean
  }

  connect() {
    this.initializeTagify()
    this.setupFormSubmissionHandler()
  }

  disconnect() {
    if (this.tagify) {
      this.tagify.destroy()
    }
  }

  initializeTagify() {
    const settings = {
      placeholder: this.placeholderValue || "Add tags...",
      maxTags: this.maxTagsValue || Infinity,
      duplicates: this.duplicatesValue || false,
      enforceWhitelist: this.enforceWhitelistValue || false,
      // Force Tagify to use our input field and not create hidden fields
      mode: null,
      originalInputValueFormat: valuesArr => valuesArr.map(item => item.value).join(', '),
      dropdown: {
        maxItems: 20,
        classname: "tagify__inline__suggestions",
        enabled: 0,
        closeOnSelect: false,
        ...this.dropdownValue
      }
    }

    // Add whitelist if provided
    if (this.whitelistValue && this.whitelistValue.length > 0) {
      settings.whitelist = this.whitelistValue
    }

    // Initialize Tagify
    this.tagify = new Tagify(this.inputTarget, settings)

    // Parse initial value from comma-separated string only
    if (this.inputTarget.value) {
      const value = this.inputTarget.value.trim()
      const initialTags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      
      if (initialTags.length > 0) {
        this.tagify.addTags(initialTags)
      }
    }

    // Set up event listeners
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Listen for tag changes and update the hidden input
    this.tagify.on('add remove', (e) => {
      this.updateHiddenInput()
    })

    // Optional: Listen for input events for autocomplete
    this.tagify.on('input', (e) => {
      // You can implement dynamic loading of suggestions here
      // For example, fetch from an API endpoint
    })
  }

  updateHiddenInput() {
    // Update the original input with comma-separated values
    const tags = this.tagify.value.map(tag => tag.value)
    const tagString = tags.join(', ')
    
    this.inputTarget.value = tagString
    
    // Trigger a change event so Rails form handling works properly
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Public method to add tags programmatically
  addTag(tagValue) {
    this.tagify.addTags(tagValue)
  }

  // Public method to remove all tags
  clearTags() {
    this.tagify.removeAllTags()
  }

  // Public method to get current tags
  getTags() {
    return this.tagify.value.map(tag => tag.value)
  }

  setupFormSubmissionHandler() {
    // Find the form that contains this input
    const form = this.inputTarget.closest('form')
    if (form) {
      form.addEventListener('submit', (e) => {
        // Ensure the input has the correct comma-separated value before submission
        this.updateHiddenInput()
        
        // Remove any hidden inputs that Tagify might have created
        const hiddenInputs = form.querySelectorAll(`input[name="${this.inputTarget.name}"][type="hidden"]`)
        hiddenInputs.forEach(hidden => {
          hidden.remove()
        })
        
        // Ensure our visible input is enabled and has the right value
        this.inputTarget.disabled = false
        this.inputTarget.type = 'text'
      })
    }
  }
}